const CONTAINER_SELECTOR = '.ti-reviews-container';
const WIDGET_SELECTOR = '.ti-widget';
const CONTROLS_SELECTOR = '.ti-controls';
const ITEM_SELECTOR = '.ti-review-item:not([style*="display: none"])';
const WRAPPER_SELECTOR = '.ti-reviews-container-wrapper';
const TEXT_SELECTOR = '.ti-review-text-container';
const NEXT_SELECTOR = '.ti-controls .ti-next';
const PREV_SELECTOR = '.ti-controls .ti-prev';
const DRAGGING_CLASS = 'ti-reviews-container--dragging';
const CLICK_THRESHOLD = 5;
const SWIPE_THRESHOLD = 25;
const SNAP_BACK_DURATION = 120;
const SLIDE_DURATION = 300;

const drag = {
  container: null,
  slider: null,
  itemWidth: 0,
  startX: 0,
  startY: 0,
  deltaX: 0,
  position: null,
  hasMoved: false,
};

let suppressNextClick = false;

const onClickCapture = (event) => {
  if (!suppressNextClick || !event.isTrusted) {
    return;
  }

  suppressNextClick = false;

  event.preventDefault();
  event.stopPropagation();
};

const getSlider = (container) => {
  const widget = container.closest(WIDGET_SELECTOR);
  const slider = widget && widget.TrustindexSliderWidget;

  if (!slider || typeof slider.animate !== 'function' || typeof slider.move !== 'function') {
    return null;
  }

  return slider;
};

const slideWithControls = (container, direction) => {
  const widget = container.closest(WIDGET_SELECTOR);

  if (!widget) {
    return;
  }

  const button = widget.querySelector(
    direction === 'next' ? NEXT_SELECTOR : PREV_SELECTOR
  );

  if (!button || button.style.display === 'none') {
    return;
  }

  button.click();
};

const settle = (snapshot, direction) => {
  const { slider, container, deltaX, itemWidth, position } = snapshot;
  const origin = parseInt(slider.position, 10) || 0;
  const steps = Math.ceil(Math.abs(deltaX) / itemWidth);
  const canMove = direction === 'next' ? slider.isNext : slider.isPrev;

  container.querySelectorAll(TEXT_SELECTOR).forEach((text) => (text.scrollTop = 0));

  if (!canMove) {
    slider.animate(`${position}px`, `${origin}px`, SNAP_BACK_DURATION);
    window.setTimeout(
      () => slider.move(direction, 'manual', 400, steps),
      SNAP_BACK_DURATION
    );

    return;
  }

  slider.position = `${position}px`;

  const wrapper =
    (slider.widget && slider.widget.reviewsContainerWrapper) ||
    container.querySelector(WRAPPER_SELECTOR);
  const target =
    (-1 * (slider.state + steps * (direction === 'next' ? 1 : -1)) * wrapper.offsetWidth) /
    slider.visibleReviewNum;
  const duration = Math.round((Math.abs(target - position) / itemWidth) * SLIDE_DURATION);

  slider.move(direction, 'manual', duration, steps);
};

const onPointerMove = (event) => {
  drag.deltaX = event.clientX - drag.startX;

  const deltaY = event.clientY - drag.startY;

  if (!drag.hasMoved && Math.abs(deltaY) > Math.abs(drag.deltaX)) {
    stopDrag();

    return;
  }

  if (!drag.hasMoved && Math.abs(drag.deltaX) > CLICK_THRESHOLD) {
    drag.hasMoved = true;
    drag.container.classList.add(DRAGGING_CLASS);
  }

  if (!drag.hasMoved || !drag.slider || drag.slider.isMoving) {
    return;
  }

  drag.position = (parseInt(drag.slider.position, 10) || 0) + drag.deltaX;
  drag.slider.animate(drag.slider.position, `${drag.position}px`, 0);

  if (drag.slider.isLoop && typeof drag.slider.addClonedReviews === 'function') {
    const direction = drag.deltaX < 0 ? 'next' : 'prev';
    const steps =
      (direction === 'prev' ? -1 : 1) * Math.ceil(Math.abs(drag.deltaX) / drag.itemWidth);

    drag.slider.addClonedReviews(drag.slider.state + steps, direction);
  }
};

const onPointerUp = () => {
  const snapshot = {
    container: drag.container,
    slider: drag.slider,
    itemWidth: drag.itemWidth,
    deltaX: drag.deltaX,
    position: drag.position,
  };
  const { container, slider, deltaX, position } = snapshot;
  const { hasMoved } = drag;

  stopDrag();

  if (!hasMoved) {
    return;
  }

  suppressNextClick = true;
  window.setTimeout(() => (suppressNextClick = false), 0);

  if (!slider) {
    if (Math.abs(deltaX) >= SWIPE_THRESHOLD) {
      slideWithControls(container, deltaX < 0 ? 'next' : 'prev');
    }

    return;
  }

  if (Math.abs(deltaX) > SWIPE_THRESHOLD && position !== null) {
    settle(snapshot, deltaX < 0 ? 'next' : 'prev');
  } else if (position !== null && position !== (parseInt(slider.position, 10) || 0)) {
    slider.animate(`${position}px`, `${parseInt(slider.position, 10) || 0}px`, SNAP_BACK_DURATION);
  }
};

function stopDrag() {
  if (!drag.container) {
    return;
  }

  drag.container.classList.remove(DRAGGING_CLASS);

  document.removeEventListener('pointermove', onPointerMove);
  document.removeEventListener('pointerup', onPointerUp);
  document.removeEventListener('pointercancel', onPointerUp);
  document.removeEventListener('dragstart', preventDragStart);

  drag.container = null;
  drag.slider = null;
  drag.position = null;
  drag.hasMoved = false;
}

function preventDragStart(event) {
  event.preventDefault();
}

const onPointerDown = (event) => {
  if (event.pointerType !== 'mouse' || event.button !== 0) {
    return;
  }

  const container = event.target.closest(CONTAINER_SELECTOR);

  if (!container || event.target.closest(CONTROLS_SELECTOR)) {
    return;
  }

  const item = container.querySelector(ITEM_SELECTOR);

  stopDrag();

  drag.container = container;
  drag.slider = getSlider(container);
  drag.itemWidth = item ? item.offsetWidth : 0;
  drag.startX = event.clientX;
  drag.startY = event.clientY;
  drag.deltaX = 0;
  drag.position = null;
  drag.hasMoved = false;

  if (!drag.itemWidth) {
    drag.slider = null;
  }

  document.addEventListener('pointermove', onPointerMove);
  document.addEventListener('pointerup', onPointerUp);
  document.addEventListener('pointercancel', onPointerUp);
  document.addEventListener('dragstart', preventDragStart);
};

const trustindexDrag = () => {
  if (!('PointerEvent' in window)) {
    return;
  }

  document.addEventListener('pointerdown', onPointerDown);
  window.addEventListener('click', onClickCapture, true);
};

trustindexDrag();

export default trustindexDrag;



// WEBPACK FOOTER //
// ./scripts/global/trustindex-drag.js